/**
 * Кеш «кто вошёл» для первой отрисовки кабинета (igra.now).
 *
 * Зачем: кабинет рисовался как гостевой («Войти», без вкладок) и только после
 * ответа `GET /profile/base-info` перерисовывался в авторизованный — человек
 * видел мигание со своим аккаунтом в конце. Здесь лежит ровно то, что нужно
 * шапке и навигации, чтобы нарисовать своё состояние сразу, не дожидаясь сети.
 *
 * Чем это НЕ является: источником правды о доступе. Ни один допуск не решается
 * по этой записи — она только убирает мигание, пока летит запрос. Ответ сервера
 * всегда главнее: пришло «не авторизован» — запись снимается, человек уходит на
 * гостевой экран (см. `handleUnauthorized` в shared/api/httpClient.ts).
 *
 * Состав: имя, почта, аватар и статус доступа — то, что рисует шапка. Ни
 * кошелька, ни реф-ссылки, ни блока security: чего нет в записи, то и не утечёт
 * из чужого браузера, а лишний объём кеша всё равно пришлось бы обновлять.
 *
 * Версия: запись чужой версии игнорируется целиком. Без этого смена формы
 * данных роняла бы отрисовку у всех, кто уже открывал сайт со старым кешем.
 *
 * Любое обращение к localStorage обёрнуто в try/catch: приватное окно и
 * «блокировать данные сайтов» бросают на самом обращении (тот же приём, что в
 * shared/consent.ts).
 */
import type { ProfileBaseInfo } from '@/modules/profile/types/profile'

/** Поднимать при любом изменении состава полей записи. */
export const PROFILE_CACHE_VERSION = 1

const STORAGE_KEY = 'igra_profile_v1'

export interface ProfileCacheEntry {
  /** Версия формы записи; чужая версия — запись не читается. */
  v: number
  id: number
  email: string
  login: string
  first_name: string
  last_name: string
  photo: string | null
  access: number
  access_title: string
}

const toEntry = (profile: ProfileBaseInfo): ProfileCacheEntry => ({
  v: PROFILE_CACHE_VERSION,
  id: profile.id,
  email: profile.email ?? '',
  login: profile.login ?? '',
  first_name: profile.first_name ?? '',
  last_name: profile.last_name ?? '',
  photo: profile.photo ?? null,
  access: profile.access ?? 0,
  access_title: profile.access_title ?? '',
})

export function readProfileCache(): ProfileCacheEntry | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw === null) return null

    const parsed = JSON.parse(raw) as Partial<ProfileCacheEntry>
    if (parsed?.v !== PROFILE_CACHE_VERSION) return null
    // Без идентификатора запись бессмысленна: это не «кто-то вошёл», а мусор.
    if (typeof parsed.id !== 'number' || parsed.id <= 0) return null

    return {
      v: PROFILE_CACHE_VERSION,
      id: parsed.id,
      email: typeof parsed.email === 'string' ? parsed.email : '',
      login: typeof parsed.login === 'string' ? parsed.login : '',
      first_name: typeof parsed.first_name === 'string' ? parsed.first_name : '',
      last_name: typeof parsed.last_name === 'string' ? parsed.last_name : '',
      photo: typeof parsed.photo === 'string' ? parsed.photo : null,
      access: typeof parsed.access === 'number' ? parsed.access : 0,
      access_title: typeof parsed.access_title === 'string' ? parsed.access_title : '',
    }
  } catch {
    return null
  }
}

/**
 * Кладёт свежий ответ `base-info` в кеш и возвращает то, что положено.
 * Возвращает `null`, если класть нечего (профиль без id) — такой ответ не
 * доказывает, что человек вошёл.
 */
export function writeProfileCache(profile: ProfileBaseInfo): ProfileCacheEntry | null {
  if (typeof profile?.id !== 'number' || profile.id <= 0) return null

  const entry = toEntry(profile)
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entry))
  } catch {
    // Не сохранилось — в следующий раз просто снова будет мигание. Это лучше,
    // чем ронять загрузку кабинета из-за запрета на хранилище.
  }

  return entry
}

export function clearProfileCache(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Хранилище недоступно — значит, и записи в нём нет.
  }
}
